import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { appConfigApi } from '@/api/appConfig';
import { Card, CardTitle } from '@/components/ui/Card';
import { humanizeError } from '@/lib/errors';
import {
  BITRATE_CHOICES,
  OFFLINE_RECORDS_KEY,
  RECORD_BITRATE_KEY,
  RECORD_CLIENT_ID_KEY,
  readRecordVoiceFlags,
  type BitrateChoice,
} from '@/lib/recordVoiceFlags';
import { cn } from '@/lib/utils';

/**
 * «Запись голоса» в настройках приложения (промпт 22, «да» владельца 18.10.2026): номер у каждой записи, хранение
 * записей файлом, сжатие. Каждое изменение — сразу на сервер по одному ключу, как у соседних переключателей.
 *
 * ⚠️ БЕЗ ТЕХНИЧЕСКИХ НАЗВАНИЙ И НОМЕРОВ ТЕХДОЛГА (решение владельца 18.10): ни ключей настроек, ни «шагов 7, 9…» в
 * тексте. Предупреждение «сжатие не включать» снято 28.10.2026 после проверки на телефоне (`lib/recordVoiceFlags.ts`).
 * ⚠️ Ключа нет — «выключено» (`readRecordVoiceFlags`), а не «включено», как в общем списке возможностей.
 */
export function RecordVoiceCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const config = useQuery({ queryKey: ['admin', 'app-config'], queryFn: appConfigApi.get });
  const set = useMutation({
    mutationFn: ({ key, value }: { key: string; value: unknown }) => appConfigApi.set(key, value),
    onSuccess: (doc) => qc.setQueryData(['admin', 'app-config'], doc),
  });
  const flags = readRecordVoiceFlags(config.data);
  const disabled = config.isLoading || set.isPending;

  const BITRATE_LABEL: Record<BitrateChoice, string> = {
    0: t('adminRecordVoice.bitrateOff'),
    32000: t('adminRecordVoice.bitrate32'),
    64000: t('adminRecordVoice.bitrate64'),
  };

  return (
    <Card data-record-voice-card>
      <CardTitle>{t('adminRecordVoice.title')}</CardTitle>
      <ul className="mt-3 divide-y divide-outline-variant">
        <SwitchRow
          title={t('adminRecordVoice.clientIdTitle')}
          desc={t('adminRecordVoice.clientIdDesc')}
          on={flags.clientId}
          disabled={disabled}
          onToggle={() => set.mutate({ key: RECORD_CLIENT_ID_KEY, value: !flags.clientId })}
        />
        <SwitchRow
          title={t('adminRecordVoice.offlineTitle')}
          desc={t('adminRecordVoice.offlineDesc')}
          on={flags.offline}
          disabled={disabled}
          onToggle={() => set.mutate({ key: OFFLINE_RECORDS_KEY, value: !flags.offline })}
        />
        <li className="space-y-2 py-3">
          <div className="text-sm font-semibold text-on-surface">{t('adminRecordVoice.bitrateTitle')}</div>
          <p className="text-[13px] text-on-surface-variant">{t('adminRecordVoice.bitrateDesc')}</p>
          <div className="flex flex-wrap gap-1 rounded-full bg-surface-container-low p-1" role="radiogroup" aria-label={t('adminRecordVoice.bitrateTitle')}>
            {BITRATE_CHOICES.map((c) => (
              <button
                key={c}
                type="button"
                role="radio"
                aria-checked={flags.bitrate === c}
                disabled={disabled}
                onClick={() => flags.bitrate !== c && set.mutate({ key: RECORD_BITRATE_KEY, value: c })}
                className={cn(
                  'min-h-[36px] flex-1 rounded-full px-3 text-[13px] font-semibold transition',
                  flags.bitrate === c ? 'bg-surface text-on-surface shadow-sm' : 'text-on-surface-variant',
                )}
              >
                {BITRATE_LABEL[c]}
              </button>
            ))}
          </div>
          {flags.bitrateCustom && (
            <p className="text-[13px] text-on-surface-variant" data-bitrate-custom>
              {t('adminRecordVoice.bitrateCustom', { kbps: Math.round(flags.bitrate / 1000) })}
            </p>
          )}
        </li>
      </ul>
      {config.isError && <p className="mt-2 text-sm text-error">{humanizeError(config.error)}</p>}
      {set.isError && <p className="mt-2 text-sm text-error">{humanizeError(set.error)}</p>}
    </Card>
  );
}

function SwitchRow({
  title,
  desc,
  on,
  disabled,
  onToggle,
}: {
  title: string;
  desc: string;
  on: boolean;
  disabled: boolean;
  onToggle: () => void;
}) {
  return (
    <li className="flex items-start justify-between gap-4 py-3">
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-on-surface">{title}</span>
        <span className="mt-0.5 block text-[13px] text-on-surface-variant">{desc}</span>
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={title}
        disabled={disabled}
        onClick={onToggle}
        className={cn(
          'relative mt-0.5 h-6 w-11 flex-none rounded-full transition-colors',
          on ? 'bg-primary' : 'bg-outline-variant',
          disabled && 'opacity-50',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 h-5 w-5 rounded-full bg-surface transition-all',
            on ? 'left-[calc(100%-1.375rem)]' : 'left-0.5',
          )}
        />
      </button>
    </li>
  );
}
